import { Button } from "@/components/ui/Button";
import { Text } from "@/components/ui/Text";
import { PlugZap } from "lucide-react";
import { useEffect } from "react";
import { PlaygroundChat } from "./PlaygroundChat";
import { useProxiedMcpConnection } from "./useProxiedMcpConnection";

interface PlaygroundProxiedChatProps {
  mcpServerId: string;
  userSessionIssuerId: string | undefined;
  environmentSlug: string | null;
  model: string;
  additionalActions?: React.ReactNode;
}

/**
 * The proxied-MCP-backed playground variant: resolves the server's proxied
 * `/mcp/<slug>` URL and issuer-gated token, prompts for an upstream connect
 * when needed, then renders the shared {@link PlaygroundChat}. Environment and
 * MCP-App registration don't apply to proxied servers, so both are omitted.
 */
export function PlaygroundProxiedChat({
  mcpServerId,
  userSessionIssuerId,
  environmentSlug,
  model,
  additionalActions,
}: PlaygroundProxiedChatProps): JSX.Element {
  const {
    mcpUrl,
    gatewayToken,
    needsAuth,
    isError,
    connectUrl,
    refetch,
    isLoading,
    connectionReady,
  } = useProxiedMcpConnection(mcpServerId, userSessionIssuerId);

  // When the user comes back from the connect tab, re-attempt the connection so
  // a freshly linked session surfaces without a manual refresh.
  useEffect(() => {
    if (!needsAuth) return;
    const onFocus = () => refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [needsAuth, refetch]);

  if (needsAuth && connectUrl) {
    return <ProxiedConnectPrompt connectUrl={connectUrl} />;
  }

  if (needsAuth) {
    return (
      <ProxiedStatusNotice message="This server has no authorization server configured, so there's no Connect flow to start. Set one up in the server's authentication settings.">
        <Button variant="secondary" onClick={refetch}>
          Try again
        </Button>
      </ProxiedStatusNotice>
    );
  }

  if (isError) {
    return (
      <ProxiedStatusNotice message="Couldn't connect to this MCP server to list its tools.">
        <Button variant="secondary" onClick={refetch}>
          Try again
        </Button>
      </ProxiedStatusNotice>
    );
  }

  if (!mcpUrl || isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Text muted>Connecting to MCP server…</Text>
      </div>
    );
  }

  // Issuer-gated server whose JWT never arrived (mint failed): block rather than
  // open an unauthenticated chat that would 401 on every request.
  if (!connectionReady) {
    return (
      <ProxiedStatusNotice message="Couldn't authenticate with this MCP server. Check the server's authorization server configuration." />
    );
  }

  return (
    <PlaygroundChat
      mcpUrl={mcpUrl}
      gatewayToken={gatewayToken}
      model={model}
      environmentSlug={environmentSlug}
      additionalActions={additionalActions}
    />
  );
}

function ProxiedStatusNotice({
  message,
  children,
}: {
  message: string;
  children?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="border-neutral-softest flex max-w-md flex-col items-center gap-3 border px-6 py-12 text-center">
        <PlugZap className="text-muted-foreground/70 size-8" />
        <Text muted className="text-sm">
          {message}
        </Text>
        {children}
      </div>
    </div>
  );
}

function ProxiedConnectPrompt({
  connectUrl,
}: {
  connectUrl: string | undefined;
}): JSX.Element {
  const handleConnect = () => {
    if (connectUrl) window.open(connectUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex h-full items-center justify-center">
      <div className="border-neutral-softest flex max-w-md flex-col items-center gap-3 border px-6 py-12 text-center">
        <PlugZap className="text-muted-foreground/70 size-8" />
        <Text className="font-medium">Connection Required</Text>
        <Text muted className="text-sm">
          Connect to this MCP server to authorize access before chatting with
          its tools.
        </Text>
        {connectUrl ? (
          <Button variant="secondary" onClick={handleConnect}>
            Connect
          </Button>
        ) : null}
      </div>
    </div>
  );
}
